import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { FormGroup } from "../../components/forms";
import TextInput from "../../components/form-fields/TextInput";
import useForm from "../../hooks/useForm";
import MultiInputField from "./components/MultiInputField";
import TextInputs from "./components/TextInputs";
import { NumberInput } from "../../components/form-fields";
import { contractorDetailsValidations } from "../../helpers/contractor/validations";
import { ContractorContext } from "./CreateContractor";
import ContractorStepNavigation from "./ContractorStepNavigation";
import ContractorHeading from "./components/Heading";
import { useNavigate } from "react-router";
import { removeExtraSpaces } from "../../helpers/shared/formHandlers";

type Props = {
  onNext: VoidFunction | null;
};

const ContractorDetails: FC<Props> = ({ onNext }) => {
  const { t } = useTranslation("pages", { keyPrefix: "contractor.newContractor.createDetails" });
  const navigate = useNavigate();
  const { state, setFormState, onChange } = useContext(ContractorContext);
  const { field, validateForm, validateField } = useForm(state, contractorDetailsValidations, onChange);
  const onPrevious = () => {
    navigate("/contractor");
  };

  const handleNext = () => validateForm(() => onNext?.());
  return (
    <div className="container-flow flex-grow">
      <ContractorHeading title="Contractor Details" />
      <FormGroup title={t("contractorDetail")}>
        <TextInput
          aria-label="company-name"
          value={state.companyName}
          {...field.companyName}
          onBlur={removeExtraSpaces("companyName", setFormState, validateField)}
          label={t("companyName")}
          disabled={false}
        />
        <TextInput
          aria-label="uen-number"
          value={state.uenNumber}
          {...field.uenNumber}
          onBlur={removeExtraSpaces("uenNo", setFormState, validateField)}
          label={t("uenNo")}
          disabled={false}
        />
      </FormGroup>
      <FormGroup title={t("companyAddress")}>
        <TextInputs
          state={state}
          setFormState={setFormState}
          validateField={validateField}
          field={field}
          fieldsName={["number", "streetName", "floorNumber", "unitNumber", "postalCode"]}
          t={t}
        />
      </FormGroup>

      <FormGroup title={t("maximumSumInsuredLimit")} className="whitespace-nowrap">
        <NumberInput min={5000} max={1000000} {...field.sumInsured} value={state.sumInsured} stepFactor={5000} />
      </FormGroup>

      <div className="flex flex-col gap-4">
        {state.directors.map((ele, index, originalInput) => (
          <MultiInputField
            formName={t("directors.title")}
            key={index}
            originalInput={originalInput}
            keyIndex={index}
            fieldToReplicate={"directors"}
            ele={ele}
            setState={setFormState}
            addBtnText={t("directors.button.addDirector")}
            areOptional={false}
            labelPrefix={t("directors.label")}
            maxCount={10}
            limitMessage={t("directors.maximumDirectorAdded")}
            {...field.directors}
          />
        ))}
      </div>

      <div className="flex flex-col gap-4">
        {state.guarantors.map((ele, index, originalInput) => (
          <MultiInputField
            fieldToReplicate={"guarantors"}
            labelPrefix={t("guarantors.label")}
            formName={t("guarantors.title")}
            key={index}
            addBtnText={t("guarantors.button.addGuarantor")}
            areOptional={true}
            originalInput={originalInput}
            keyIndex={index}
            ele={ele}
            setState={setFormState}
            maxCount={10}
            limitMessage={t("guarantors.maximumGuarantorAdded")}
            {...field.guarantors}
          />
        ))}
      </div>
      <ContractorStepNavigation onNext={handleNext} onBack={onPrevious} />
    </div>
  );
};

export default ContractorDetails;
